/**
 * UpgradeModal & FeatureGate
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Star, Check, Lock } from '@mui/icons-material';
import { useBilling } from '../hooks/useBilling';

const proFeatures = [
  'myDATA Sync',
  'Export PDF/Excel',
  'AI Assistant',
  'Περισσότεροι ενεργοί υπάλληλοι',
  'Priority Support',
];

export default function UpgradeModal({
  open,
  onClose,
  title = 'Αναβάθμιση απαιτείται',
  message = 'Αυτή η λειτουργία είναι διαθέσιμη στα πλάνα Pro και Business.',
  featureName,
}) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate('/settings/billing');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Lock color="primary" />
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {message}
          {featureName ? ` (${featureName})` : ''}
        </Typography>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0,128,96,0.04)', border: '1px solid rgba(0,128,96,0.12)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Star sx={{ color: '#f5a623', fontSize: 18 }} />
            Pro & Business
          </Typography>
          <List dense disablePadding>
            {proFeatures.map((text) => (
              <ListItem key={text} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText primary={text} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Άκυρο</Button>
        <Button
          variant="contained"
          onClick={handleUpgrade}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          Δες τα πλάνα
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FeatureGate({ feature, children, fallback = null }) {
  const { hasFeature, getUpgradeMessage, isLoading } = useBilling();
  const [modalOpen, setModalOpen] = React.useState(false);

  if (isLoading) return children;

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  return (
    <>
      <Box onClick={() => setModalOpen(true)} sx={{ cursor: 'pointer' }}>
        {fallback || children}
      </Box>
      <UpgradeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        featureName={getUpgradeMessage(feature)}
        message="Αυτή η λειτουργία δεν είναι διαθέσιμη στο Free plan."
      />
    </>
  );
}
